import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarIcon, ChevronLeft, ChevronRight, Clock } from "lucide-react";
import { format, addDays, subDays } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

interface Props {
  date: Date;
  onDateChange: (d: Date) => void;
  timeSlots: string[];
  selectedTime: string | null;
  onTimeChange: (t: string) => void;
  /** Map of slot start ("HH:mm") -> total covers (non-cancelled) for the selected date. */
  slotCovers?: Map<string, number>;
}

export const V2ServiceBar = ({ date, onDateChange, timeSlots, selectedTime, onTimeChange, slotCovers }: Props) => {
  const selectedLabel = selectedTime
    ? `${selectedTime}${slotCovers?.get(selectedTime) ? ` · ${slotCovers.get(selectedTime)}c` : ''}`
    : undefined;
  return (
    <div className="flex items-center gap-1 sm:gap-1.5 flex-wrap min-w-0">
      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => onDateChange(subDays(date, 1))} aria-label="Jour précédent">
        <ChevronLeft className="h-4 w-4" />
      </Button>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className={cn("h-8 gap-1.5 px-2 sm:px-3 font-medium shrink-0")}>
            <CalendarIcon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{format(date, 'EEE d MMM', { locale: fr })}</span>
            <span className="sm:hidden">{format(date, 'd MMM', { locale: fr })}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={date} onSelect={d => d && onDateChange(d)} initialFocus locale={fr} />
        </PopoverContent>
      </Popover>
      <Button size="icon" variant="ghost" className="h-8 w-8 shrink-0" onClick={() => onDateChange(addDays(date, 1))} aria-label="Jour suivant">
        <ChevronRight className="h-4 w-4" />
      </Button>

      <div className="flex items-center gap-1 ml-1 min-w-0">
        <Clock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <Select value={selectedTime ?? undefined} onValueChange={onTimeChange}>
          <SelectTrigger className="h-8 w-[110px] sm:w-[140px]">
            <SelectValue placeholder="Créneau">{selectedLabel}</SelectValue>
          </SelectTrigger>
          <SelectContent className="max-h-[300px]">
            {timeSlots.map(t => {
              const c = slotCovers?.get(t) ?? 0;
              return (
                <SelectItem key={t} value={t}>
                  <span className="flex items-center justify-between gap-3 w-full">
                    <span className="font-mono">{t}</span>
                    <span className={cn(
                      "text-[10px] tabular-nums",
                      c > 0 ? "text-foreground font-medium" : "text-muted-foreground/50"
                    )}>
                      {c > 0 ? `${c}c` : '—'}
                    </span>
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};
